import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader, Mail, Phone, Calendar, Inbox, Lock } from 'lucide-react';
import { fetchJobApplications, updateApplicationStatus, createConversation } from '../services/api';
import { overlayDismiss } from '../utils/overlayDismiss';
import ApplicantProfile from './ApplicantProfile';
import { jobTitle } from '../constants/jobOptions';
import './ApplicationsSheet.css';

const STATUS_LIST = [
  { value: 'pending',     label: 'Gesendet' },
  { value: 'viewed',      label: 'Gesehen' },
  { value: 'shortlisted', label: 'In Prüfung' },
  { value: 'invited',     label: 'Eingeladen' },
  { value: 'accepted',    label: 'Angenommen' },
  { value: 'rejected',    label: 'Abgelehnt' },
];

// Gleiche Liste wie im Backend (applicationsController): vorher kommen Nachname,
// Kontakt, Schule und Firmen gar nicht erst mit
const PRIVACY_LIFTED = ['invited', 'accepted'];

function computeAge(birthYear) {
  if (!birthYear) return null;
  return new Date().getFullYear() - parseInt(birthYear);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ApplicationsSheet({ job, onClose }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const loadApplications = useCallback(() => fetchJobApplications(job.id)
    .then(({ data }) => setApplications(Array.isArray(data) ? data : []))
    .catch(() => setError('Bewerbungen konnten nicht geladen werden.'))
    .finally(() => setLoading(false)), [job.id]);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  const handleStatusChange = async (app, newStatus) => {
    if (app.status === newStatus || updating === app.id) return;
    setUpdating(app.id);
    try {
      await updateApplicationStatus(app.id, newStatus);
      setApplications(prev =>
        prev.map(a => a.id === app.id ? { ...a, status: newStatus } : a)
      );
      if (newStatus === 'invited') {
        try { await createConversation(app.id); } catch {}
      }
      // Freigabe geändert: Server liefert die Daten jetzt mit bzw. nicht mehr
      if (PRIVACY_LIFTED.includes(newStatus) !== PRIVACY_LIFTED.includes(app.status)) {
        await loadApplications();
      }
    } catch {
      // silent – status didn't change visually
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="apps-overlay" {...overlayDismiss(onClose)}>
      <div className="apps-sheet animate-slideInUp">
        <div className="apps-sheet__handle" />

        <div className="apps-sheet__header">
          <div className="apps-sheet__heading">
            <h2>{jobTitle(job.title)}</h2>
            <p>
              {loading ? '…' : `${applications.length} Bewerbung${applications.length !== 1 ? 'en' : ''}`}
            </p>
          </div>
          <button className="apps-sheet__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="apps-sheet__body">
          {loading && (
            <div className="apps-loading">
              <Loader size={22} className="spin" />
              <span>Bewerbungen werden geladen…</span>
            </div>
          )}

          {error && <div className="apps-error">{error}</div>}

          {!loading && !error && applications.length === 0 && (
            <div className="apps-empty">
              <div className="apps-empty__emoji"><Inbox size={40} strokeWidth={1.3} /></div>
              <p>Noch keine Bewerbungen für dieses Inserat.</p>
            </div>
          )}

          {!loading && applications.map(app => {
            const lifted = PRIVACY_LIFTED.includes(app.status);
            const age = computeAge(app.birth_year);
            const displayName = lifted
              ? `${app.first_name} ${app.last_name}`
              : app.first_name;
            const isUpdating = updating === app.id;

            return (
              <div key={app.id} className={`app-card${lifted ? ' app-card--lifted' : ''}`}>
                {/* Applicant header */}
                <div className="app-card__top">
                  <div className="app-card__avatar">
                    {app.first_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="app-card__meta">
                    <strong className="app-card__name">{displayName}</strong>
                    <span className="app-card__sub">
                      {age ? `${age} Jahre` : ''}
                      {age && app.youth_location ? ' · ' : ''}
                      {app.youth_location || ''}
                    </span>
                  </div>
                  <span className={`app-badge app-badge--${app.status || 'pending'}`}>
                    {STATUS_LIST.find(s => s.value === (app.status || 'pending'))?.label}
                  </span>
                </div>

                {/* Bewerberprofil: Schule, Ausbildung, Erfahrung, Skills, Sprachen, Führerschein, Über mich */}
                <div className="app-card__profile">
                  <ApplicantProfile profile={app} />
                </div>

                {/* Contact – only after invitation */}
                {lifted && (app.phone || app.email) && (
                  <div className="app-card__contact">
                    {app.phone && (
                      <a href={`tel:${app.phone}`} className="app-contact-row">
                        <Phone size={14} /> {app.phone}
                      </a>
                    )}
                    {app.email && (
                      <a href={`mailto:${app.email}`} className="app-contact-row">
                        <Mail size={14} /> {app.email}
                      </a>
                    )}
                  </div>
                )}

                {/* Privacy note before invitation */}
                {!lifted && (
                  <p className="app-privacy-note">
                    <Lock size={12} /> Nachname, Kontakt, Schule und Firmen werden nach Einladung sichtbar
                  </p>
                )}

                {/* Date */}
                <div className="app-card__date">
                  <Calendar size={12} /> Beworben am {formatDate(app.created_at)}
                </div>

                {/* Status change */}
                <div className="app-card__status-row">
                  {STATUS_LIST.map(({ value, label }) => (
                    <button
                      key={value}
                      className={`app-status-btn app-status-btn--${value}${app.status === value ? ' is-active' : ''}`}
                      onClick={() => handleStatusChange(app, value)}
                      disabled={isUpdating}
                    >
                      {isUpdating && app.status !== value ? label : label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
